import React from 'react';
import {
    FilterDatatable,
    FiltersDatatableList,
    SelectApiFilter,
    SelectMultipleApiFilter,
} from '@europrocurement/flexy-datatable';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName, IconProp } from '@fortawesome/fontawesome-svg-core';
import { DossierObjectGroup } from '@europrocurement/l2d-domain';
import useDossierFormalite, {
    DossierFormalite as DossierFormaliteFilter,
} from './useDossierFormalite';
import useDossierObjectGroup from './useDossierObjectGroup';
import useDossierNumero, { DossierNumero } from './useDossierNumero';
import useDossierCollection, { DossierCollection } from './useDossierCollection';
import useDossierClient, { DossierClient } from './useDossierClient';
import useDossierContact, { DossierContact } from './useDossierContact';
import useDossierValideurs, { DossierValideursStatus } from './useDossierValideurs';
import useDossierPrescriber, { DossierPrescripteurs } from './useDossierPrescriber';
import useDossierFormaliste, { DossierFormaliste } from './useDossierFormaliste';
import useDossierWorkGroup, { DossierWorkGroup } from './useDossierWorkGroup';
import useDossierOffer, { DossierOffer } from './useDossierOffer';
import useDossierGreffe, { DossierGreffe } from './useDossierGreffe';

const useDossierFilters = () => {
    const { searchDossierFormalite } = useDossierFormalite();
    const { searchDossierObjectGroup } = useDossierObjectGroup();
    const { searchDossierNumero } = useDossierNumero();
    const { searchDossierCollection } = useDossierCollection();
    const { searchDossierClient } = useDossierClient();
    const { searchDossierContact } = useDossierContact();
    const { searchDossierValideursStatus } = useDossierValideurs();
    const { searchDossierPrescriber } = useDossierPrescriber();
    const { searchDossierFormaliste } = useDossierFormaliste();
    const { searchDossierWorkGroup } = useDossierWorkGroup();
    const { searchDossierOffer } = useDossierOffer();
    const { searchDossierGreffe } = useDossierGreffe();

    const companyFilter: FilterDatatable = {
        type: 'selectApi',
        label: 'Sociétés',
        field: 'clientId',
        icon: 'building-user',
        showResultOnSearchOnly: true,
        searchFunction: searchDossierClient,
        renderItem: (item: DossierClient) => item.libelle as string,
        valueMap: (item: DossierClient) => item.id as unknown as string,
    };

    const groupFilter: FilterDatatable = {
        label: 'Mes groupes',
        field: 'groupId',
        type: 'selectApi',
        icon: 'folders',
        searchFunction: searchDossierObjectGroup,
        renderItem: (item: DossierObjectGroup) => item.label as string,
        renderIcon: (item: DossierObjectGroup, defaultIcon) => {
            const icon = item?.icon || defaultIcon;
            const color = item?.color;

            return (
                <FaOptionIcon
                    sx={{
                        fontSize: '12px',
                        lineHeight: '0',
                        color: color ? `${color}.main` : 'inherit',
                    }}
                    icon={['fasl', icon as IconName]}
                />
            );
        },
        valueMap: (item: DossierObjectGroup) => item.id as unknown as string,
    };

    const commandFilter: FilterDatatable = {
        label: 'Commandes',
        field: 'collectionId',
        type: 'selectApi',
        icon: 'file-lines',
        searchFunction: searchDossierCollection,
        renderItem: (item: DossierCollection) => item.numero as string,
        valueMap: (item: DossierCollection) => item.id,
    };

    const dossierFilter: SelectApiFilter<DossierNumero> = {
        label: 'Dossier',
        field: 'numero',
        type: 'selectApi',
        icon: 'folder',
        searchFunction: searchDossierNumero,
        renderItem: (item: DossierNumero) => item.numero as string,
        valueMap: (item: DossierNumero) => item.numero as string,
    };

    const missingDocFilter: FilterDatatable = {
        label: 'Élements manquants',
        field: 'hasMissingRequiredDocuments',
        icon: 'file',
        type: 'boolean',
    };

    const formatlitiesFilter: FilterDatatable = {
        label: 'Formalités',
        field: 'formaliteId',
        type: 'selectMultipleApi',
        icon: 'gavel',
        placeholder: 'Rechercher une formalite',
        title: 'Formalités',
        getItems: searchDossierFormalite,
        getItemLabel: (item: DossierFormaliteFilter) => item.libelle as string,
        getItemValue: (item: DossierFormaliteFilter) => item.id as unknown as string,
        getGroupLabel: (item: DossierFormaliteFilter) => item.groupementLibelle as string,
        getGroupValue: (item: DossierFormaliteFilter) => item.groupementId as unknown as string,
    };

    const offerFilter: SelectApiFilter<DossierOffer> = {
        label: 'Offre',
        field: 'offerId',
        icon: 'star',
        type: 'selectApi',
        searchFunction: searchDossierOffer,
        renderItem: (item: DossierOffer) => item.label as string,
        renderIcon: (item: DossierOffer, defaultIcon?: IconName) => (
            <FaOptionIcon icon={['fasl', item?.icon || defaultIcon] as IconProp} />
        ),
        valueMap: (item: DossierOffer) => item.id,
    };

    const createdAtFilter: FilterDatatable = {
        type: 'dateRange',
        label: 'Date de création',
        field: 'dateCreation',
        icon: 'calendar',
        displayFormat: (startDate: Date, endDate: Date) =>
            `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
    };

    const teamContactFilter: SelectApiFilter<DossierContact> = {
        label: 'Collaborateurs',
        field: 'contactId',
        type: 'selectApi',
        icon: 'circle-user',
        searchFunction: searchDossierContact,
        renderItem: (item: DossierContact) => item.nomComplet as string,
        valueMap: (item: DossierContact) => item.id as unknown as string,
    };

    const prescriberFilter: SelectApiFilter<DossierPrescripteurs> = {
        label: 'Prescripteurs',
        field: 'prescriberId',
        type: 'selectApi',
        icon: 'user',
        searchFunction: searchDossierPrescriber,
        renderItem: (item: DossierPrescripteurs) =>
            item.codeCompta ? `${item.libelle} (${item.codeCompta})` : (item.libelle as string),
        valueMap: (item: DossierPrescripteurs) => item.id as unknown as string,
    };

    const validatorsFilter: SelectMultipleApiFilter<DossierValideursStatus> = {
        label: 'Valideurs',
        field: 'statutValideurId',
        type: 'selectMultipleApi',
        icon: 'building-columns',
        placeholder: 'Rechercher un valideur',
        title: 'Valideurs',
        getItems: searchDossierValideursStatus,
        getItemLabel: (item: DossierValideursStatus) => item.libelleStatus as string,
        getItemValue: (item: DossierValideursStatus) => item.idStatus as unknown as string,
        getGroupLabel: (item: DossierValideursStatus) => item.libelleValideur as string,
        getGroupValue: (item: DossierValideursStatus) => item.idValideur as unknown as string,
    };

    const formalisteFilter: SelectApiFilter<DossierFormaliste> = {
        label: 'Formaliste',
        field: 'formalisteId',
        icon: 'circle-user',
        type: 'selectApi',
        searchFunction: searchDossierFormaliste,
        renderItem: (item: DossierFormaliste) => item.nomComplet as string,
        valueMap: (item: DossierFormaliste) => item.id,
    };

    const workGroupFilter: SelectApiFilter<DossierWorkGroup> = {
        label: 'Groupe de travail',
        field: 'workGroupId',
        icon: 'user-group-simple',
        type: 'selectApi',
        searchFunction: searchDossierWorkGroup,
        renderItem: (item: DossierWorkGroup) => item.label as string,
        valueMap: (item: DossierWorkGroup) => item.id,
    };

    const greffeFilter: SelectApiFilter<DossierGreffe> = {
        label: 'Greffe',
        field: 'greffeId',
        icon: 'scale-balanced',
        type: 'selectApi',
        searchFunction: searchDossierGreffe,
        renderItem: (item: DossierGreffe) => item.label as string,
        valueMap: (item: DossierGreffe) => item.id,
    };

    const frontDossierFilter: FiltersDatatableList = [
        companyFilter,
        groupFilter,
        commandFilter,
        dossierFilter,
        missingDocFilter,
        formatlitiesFilter,
        createdAtFilter,
        teamContactFilter,
    ];

    const backDossierFilter: FiltersDatatableList = [
        dossierFilter,
        commandFilter,
        companyFilter,
        prescriberFilter,
        formalisteFilter,
        workGroupFilter,
        validatorsFilter,
        missingDocFilter,
        formatlitiesFilter,
        offerFilter,
        createdAtFilter,
        teamContactFilter,
        greffeFilter,
    ];

    return { frontDossierFilter, backDossierFilter };
};

export default useDossierFilters;
